import { CellItem, CellType, Layout } from './RecyclerGridView/types';
import {
    EPGChannel,
    EPGChannels,
    EPGConfig,
    EPGProgram,
    EPGPrograms,
} from './types';

export const hoursInMilliseconds = (hours: number = 1) =>
    hours * 60 * 60 * 1000;

export const convertTimeToOffset = (
    startTime: number,
    time: number,
    hourWidth: number,
) => {
    return ((time - startTime) / hoursInMilliseconds()) * hourWidth;
};

export const convertOffsetToTime = (
    startTime: number,
    offset: number,
    hourWidth: number,
) => {
    return startTime + (offset / hourWidth) * 1000 * 60 * 60;
};

const LOADING_CELL_ID_PREFIX = 'l-';
export const idIsLoadingCellId = (cellId: string) =>
    cellId.startsWith(LOADING_CELL_ID_PREFIX);
export const channelIdFromLoadingCellId = (cellId: string) =>
    cellId.substring(LOADING_CELL_ID_PREFIX.length);

const NODATA_CELL_ID_PREFIX = 'na-';
export const createNoDataCellIdFromChannelId = (channelId: string) => {
    return `${NODATA_CELL_ID_PREFIX}${channelId}`;
};
export const idIsNoDataCellId = (cellId: string) =>
    cellId.startsWith(NODATA_CELL_ID_PREFIX);

export const createLoadingItem = (
    channelId: string,
    rowIndex: number,
    rowHeight: number,
    timelineWidth: number,
): { item: CellItem; layout: Layout } => ({
    item: {
        id: `${LOADING_CELL_ID_PREFIX}${channelId}`,
        rowIndex,
        type: CellType.Loading,
    },
    layout: {
        x: 0,
        y: rowIndex * rowHeight,
        width: timelineWidth,
        height: rowHeight,
        type: CellType.Loading,
    },
});

const createNoDataItem = (
    channelId: string,
    rowIndex: number,
    rowHeight: number,
    timelineWidth: number,
): { item: CellItem; layout: Layout } => ({
    item: {
        id: `${NODATA_CELL_ID_PREFIX}${channelId}`,
        rowIndex,
        type: CellType.NoData,
    },
    layout: {
        x: 0,
        y: rowIndex * rowHeight,
        width: timelineWidth,
        height: rowHeight,
        type: CellType.NoData,
    },
});

const createProgramItem = (
    program: EPGProgram<any>,
    timelineStart: number,
    timelineEnd: number,
    rowIndex: number,
    rowHeight: number,
    hourWidth: number,
): { item: CellItem; layout: Layout } => {
    const programStart = Math.max(program.startTime, timelineStart);
    const programEnd = Math.min(program.endTime, timelineEnd);
    return {
        item: {
            id: program.id,
            rowIndex,
            type: CellType.Program,
            program,
            startTime: programStart,
            endTime: programEnd,
        },
        layout: {
            x: convertTimeToOffset(timelineStart, programStart, hourWidth),
            y: rowIndex * rowHeight,
            width: convertTimeToOffset(programStart, programEnd, hourWidth),
            height: rowHeight,
            type: CellType.Program,
        },
    };
};

const createEmptyItem = (
    channelId: string,
    from: number,
    to: number,
    index: number,
    rowIndex: number,
    rowHeight: number,
    timelineStart: number,
    hourWidth: number,
): { item: CellItem; layout: Layout } => ({
    item: {
        id: `${NODATA_CELL_ID_PREFIX}${channelId}-${index}`,
        rowIndex,
        type: CellType.MissingData,
        startTime: from,
        endTime: to,
    },
    layout: {
        x: convertTimeToOffset(timelineStart, from, hourWidth),
        y: rowIndex * rowHeight,
        width: convertTimeToOffset(from, to, hourWidth),
        height: rowHeight,
        type: CellType.MissingData,
    },
});

// TODO: somehow recalculate only for new programs,
// not the whole grid on every new program fetch
export const createProgramsGrid = (
    channels: EPGChannels<any>,
    programs: EPGPrograms<any>,
    timelineStart: number,
    config: EPGConfig,
): { data: CellItem[][]; layouts: Layout[][] } => {
    const data: CellItem[][] = [];
    const layouts: Layout[][] = [];

    const { hourWidth, hoursCount, rowHeight, padding } = config;
    const rowHeightWithPadding = rowHeight + padding;
    const timelineEnd =
        timelineStart + hoursInMilliseconds(config.hoursCount) - 1;
    const timelineWidth = hourWidth * hoursCount;

    channels.forEach(({ id: channelId }: EPGChannel<any>, rowIndex: number) => {
        const channelPrograms = programs[channelId];
        if (!channelPrograms) {
            const { item, layout } = createLoadingItem(
                channelId,
                rowIndex,
                rowHeightWithPadding,
                timelineWidth,
            );
            data.push([item]);
            layouts.push([layout]);
        } else if (!channelPrograms.length) {
            const { item, layout } = createNoDataItem(
                channelId,
                rowIndex,
                rowHeightWithPadding,
                timelineWidth,
            );
            data.push([item]);
            layouts.push([layout]);
        } else {
            // Remove overlapping programs
            let lastKeptProgram: EPGProgram<any> | undefined;
            const rowItems: CellItem[] = [];
            const rowLayouts: Layout[] = [];
            channelPrograms.forEach((program, pIndex) => {
                if (!lastKeptProgram) {
                    // Missing data from beginning
                    if (program.startTime > timelineStart) {
                        const { item, layout } = createEmptyItem(
                            channelId,
                            timelineStart,
                            program.startTime,
                            pIndex,
                            rowIndex,
                            rowHeightWithPadding,
                            timelineStart,
                            hourWidth,
                        );
                        rowItems.push(item);
                        rowLayouts.push(layout);
                    }
                    const { item, layout } = createProgramItem(
                        program,
                        timelineStart,
                        timelineEnd,
                        rowIndex,
                        rowHeightWithPadding,
                        hourWidth,
                    );
                    rowItems.push(item);
                    rowLayouts.push(layout);
                    lastKeptProgram = program;
                } else {
                    // Missing data from between
                    if (program.startTime > lastKeptProgram.endTime) {
                        const { item, layout } = createEmptyItem(
                            channelId,
                            lastKeptProgram.endTime,
                            program.startTime,
                            pIndex,
                            rowIndex,
                            rowHeightWithPadding,
                            timelineStart,
                            hourWidth,
                        );
                        rowItems.push(item);
                        rowLayouts.push(layout);
                    }

                    // We will skip program if overlaps with previous one
                    if (program.startTime >= lastKeptProgram.endTime) {
                        const { item, layout } = createProgramItem(
                            program,
                            timelineStart,
                            timelineEnd,
                            rowIndex,
                            rowHeightWithPadding,
                            hourWidth,
                        );
                        rowItems.push(item);
                        rowLayouts.push(layout);
                        lastKeptProgram = program;
                    }

                    // Missing data from end
                    if (
                        channelPrograms.length - 1 === pIndex &&
                        lastKeptProgram.endTime < timelineEnd
                    ) {
                        const { item, layout } = createEmptyItem(
                            channelId,
                            lastKeptProgram.endTime,
                            timelineEnd,
                            pIndex,
                            rowIndex,
                            rowHeightWithPadding,
                            timelineStart,
                            hourWidth,
                        );
                        rowItems.push(item);
                        rowLayouts.push(layout);
                    }
                }
            });
            data.push(rowItems);
            layouts.push(rowLayouts);
        }
    });

    return { data, layouts };
};
